import React from 'react';
import { Link } from 'react-router-dom';
import { 
  FaCalendarAlt, FaBolt, FaFire, FaCar, FaTruck, FaSnowflake, FaTint, 
  FaTrashAlt, FaShoppingCart, FaBuilding, FaPlane, FaIndustry
} from 'react-icons/fa';

const ApiTestPage = () => {
  const baseUrl = window.location.origin;
  
  const apiConfigs = {
    1: { name: 'Organization & Reporting Context', icon: FaCalendarAlt, color: 'blue' },
    2: { name: 'Grid Electricity Consumption', icon: FaBolt, color: 'yellow' },
    3: { name: 'Stationary Fuels', icon: FaFire, color: 'red' },
    4: { name: 'Mobile Fuels', icon: FaCar, color: 'orange' },
    5: { name: 'Logistics & Distribution', icon: FaTruck, color: 'purple' },
    6: { name: 'Refrigerants & Process Gases', icon: FaSnowflake, color: 'cyan' },
    7: { name: 'Water & Wastewater', icon: FaTint, color: 'blue' },
    8: { name: 'Waste Management', icon: FaTrashAlt, color: 'gray' },
    9: { name: 'Purchased Goods & Services', icon: FaShoppingCart, color: 'green' },
    10: { name: 'Capital Goods', icon: FaBuilding, color: 'indigo' },
    11: { name: 'Business Travel & Commuting', icon: FaPlane, color: 'pink' },
    12: { name: 'Process Emissions', icon: FaIndustry, color: 'brown' }
  };

  const colorClasses = {
    blue: 'bg-blue-100 hover:bg-blue-200 text-blue-800',
    yellow: 'bg-yellow-100 hover:bg-yellow-200 text-yellow-800',
    red: 'bg-red-100 hover:bg-red-200 text-red-800',
    orange: 'bg-orange-100 hover:bg-orange-200 text-orange-800',
    purple: 'bg-purple-100 hover:bg-purple-200 text-purple-800',
    cyan: 'bg-cyan-100 hover:bg-cyan-200 text-cyan-800',
    gray: 'bg-gray-100 hover:bg-gray-200 text-gray-800',
    green: 'bg-green-100 hover:bg-green-200 text-green-800',
    indigo: 'bg-indigo-100 hover:bg-indigo-200 text-indigo-800',
    pink: 'bg-pink-100 hover:bg-pink-200 text-pink-800',
    brown: 'bg-amber-100 hover:bg-amber-200 text-amber-800'
  };

  const allApiNumbers = Object.keys(apiConfigs).map(Number);
  const allApisUrl = `/api/unified?apis=${allApiNumbers.join(',')}`;

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">API Testing Dashboard</h1>
          <p className="text-gray-600">Test all 12 Unified API Components</p>
        </div>

        {/* Test All APIs Link */}
        <div className="mb-8 p-6 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-lg shadow-lg">
          <h2 className="text-2xl font-semibold text-white mb-4">Test All APIs Together</h2>
          <Link 
            to={allApisUrl}
            className="inline-block px-6 py-3 bg-white text-purple-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-md"
          >
            Test All 12 APIs (Unified View)
          </Link>
          <p className="mt-2 text-purple-100 text-sm">
            URL: {baseUrl}{allApisUrl}
          </p>
        </div>

        {/* Individual API Links */}
        <div className="mb-6">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">Test Individual APIs</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(apiConfigs).map(([apiNum, config]) => {
              const Icon = config.icon;
              const colorClass = colorClasses[config.color];
              const apiUrl = `/api/${apiNum}`;
              
              return (
                <div 
                  key={apiNum}
                  className={`${colorClass} rounded-lg p-6 shadow-md transition-all hover:shadow-lg`}
                >
                  <div className="flex items-center mb-3">
                    <Icon className="text-2xl mr-3" />
                    <h3 className="text-lg font-semibold">API {apiNum}</h3>
                  </div>
                  <p className="text-sm mb-4 opacity-90">{config.name}</p>
                  <Link 
                    to={apiUrl}
                    className="inline-block px-4 py-2 bg-white rounded-md font-medium hover:bg-gray-50 transition-colors text-sm"
                  >
                    Test API {apiNum}
                  </Link>
                  <p className="mt-2 text-xs opacity-75 break-all">
                    {baseUrl}{apiUrl}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Quick Links Section */}
        <div className="mt-8 p-6 bg-white rounded-lg shadow-md">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Quick Test Links</h2>
          <div className="space-y-2">
            <div>
              <strong>Test Multiple APIs:</strong>
              <ul className="list-disc list-inside ml-4 mt-1 space-y-1 text-sm text-gray-600">
                <li>
                  <Link to="/api/unified?apis=1,2,3" className="text-blue-600 hover:underline">
                    APIs 1, 2, 3: {baseUrl}/api/unified?apis=1,2,3
                  </Link>
                </li>
                <li>
                  <Link to="/api/unified?apis=4,5,6" className="text-blue-600 hover:underline">
                    APIs 4, 5, 6: {baseUrl}/api/unified?apis=4,5,6
                  </Link>
                </li>
                <li>
                  <Link to="/api/unified?apis=7,8,9" className="text-blue-600 hover:underline">
                    APIs 7, 8, 9: {baseUrl}/api/unified?apis=7,8,9
                  </Link>
                </li>
                <li>
                  <Link to="/api/unified?apis=10,11,12" className="text-blue-600 hover:underline">
                    APIs 10, 11, 12: {baseUrl}/api/unified?apis=10,11,12
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Backend API Endpoints Reference */}
        <div className="mt-8 p-6 bg-gray-800 rounded-lg shadow-md text-white">
          <h2 className="text-xl font-semibold mb-4">Backend API Endpoints Reference</h2>
          <div className="space-y-2 text-sm font-mono">
            <div>
              <strong className="text-green-400">GET</strong> /api-questions/category/:category
            </div>
            <div>
              <strong className="text-green-400">GET</strong> /api-questions/questions/:questionId/api-questions
            </div>
            <div>
              <strong className="text-blue-400">POST</strong> /api-questions/api-questions
            </div>
            <div>
              <strong className="text-yellow-400">PUT</strong> /api-questions/api-questions/:id
            </div>
            <div>
              <strong className="text-red-400">DELETE</strong> /api-questions/api-questions/:id
            </div>
            <div>
              <strong className="text-blue-400">POST</strong> /api-questions/submit-api-question-answer
            </div>
            <div>
              <strong className="text-blue-400">POST</strong> /api-questions/trigger-api-call
            </div>
            <p className="mt-3 text-xs text-gray-400">
              Backend runs on: http://localhost:3051
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApiTestPage;


